import { useState, useEffect } from 'react';
import { ApiLocation, fetchSensorData, SensorReadings, formatTimestamp, prepareChartData } from '../../services/api';
import { getMockOrMergedData } from '../../services/mockData';
import ParameterCard from './ParameterCard';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import LoadingIndicator from './LoadingIndicator';

// Data source flags
const USE_FALLBACK_DATA = true;       // Fill missing sensors with mock data when API is partial/down
const FORCE_MOCK_DATA = false;        // Set to true to force mock data regardless of API status
const DEBUG_MODE = false;             // Set to true to show debug info in UI
const PREFER_LIVE_DATA = true;        // Use live API data as-is without merging with mock data
const FORCE_LIVE_DATA = false;        // Set to true to disable all fallbacks (useful for API debugging)

type LiveSiteDetailsProps = {
  location: ApiLocation;
  refreshTrigger: number; // New prop to trigger data refresh
  onParameterClick: (parameter: {
    label: string;
    value: string | null;
    unit?: string | null;
    description: string;
    chartData?: Array<{ time: string; value: number }>;
  }) => void;
}

export default function LiveSiteDetails({ location, refreshTrigger, onParameterClick }: LiveSiteDetailsProps) {
  const [sensorData, setSensorData] = useState<SensorReadings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('Loading...');
  const [retryCount, setRetryCount] = useState(0); // Restored for auto-retry logic
  const [usingMockData, setUsingMockData] = useState(false);
  const [dataSource, setDataSource] = useState<'api' | 'mock' | 'mixed'>('api');
  const [activeTab, setActiveTab] = useState<'parameters' | 'analysis'>('parameters');

  debug('active tab', activeTab)
  // Fetch sensor data on component mount and when location changes
  useEffect(() => {
    let isMounted = true;
    let retryTimeoutId: number | null = null; // Renamed to avoid conflict, stores timeout ID
    
    async function loadSensorData() {
      try {
        if (isMounted) {
          setIsLoading(true);
          setError(null);
          setUsingMockData(false);
          setDataSource('api');
        }
        
        // Force mock data in development if needed
        if (FORCE_MOCK_DATA && !FORCE_LIVE_DATA) {
          debug("🔄 Using FORCED mock data (FORCE_MOCK_DATA=true)");
          const mockData = getMockOrMergedData(location.ID);
          if (isMounted) {
            setSensorData(mockData);
            setIsLoading(false);
            setLastUpdated('Using forced mock data');
            setUsingMockData(true);
            setDataSource('mock');
          }
          return;
        }
        
        debug("🔄 Attempting to fetch LIVE data from API...");
        
        // Fetch real API data
        debug("📡 Calling fetchSensorData for location:", location.ID);
        const apiData = await fetchSensorData(location.ID);
        debug("📦 LiveSiteDetails: Received apiData from fetchSensorData:", JSON.stringify(apiData, null, 2));
        debug("🔍 API data keys received:", Object.keys(apiData));
        debug("🔢 Number of sensors from API:", Object.keys(apiData).length);
        
        if (isMounted) {
          // Check if we got any API data
          if (Object.keys(apiData).length === 0) {
            debug("❌ No sensor data received from API");
            
            if (FORCE_LIVE_DATA) {
              debug("⚠️ FORCE_LIVE_DATA is true - not falling back to mock data");
              setError('API returned no data. Live data mode is forced so not using fallback data.');
              setLastUpdated('No data available');
              setIsLoading(false);
            } else if (USE_FALLBACK_DATA) {
              // Use mock data as fallback if API returned empty data
              debug("🔄 Falling back to mock data");
              const mockData = getMockOrMergedData(location.ID);
              setSensorData(mockData);
              setLastUpdated('Using simulated data (API returned no data)');
              setUsingMockData(true);
              setDataSource('mock');
            } else {
              setError('No sensor data available for this location.');
              setLastUpdated('No data available');
            }
          } else {
            // If PREFER_LIVE_DATA is true, we use API data as-is without merging with mock data
            if (PREFER_LIVE_DATA) {
              debug("Using live API data:", Object.keys(apiData));
              setSensorData(apiData);
              setDataSource('api');
              
              // Look for the most recent valid timestamp from any sensor
              const validTimestamps = Object.values(apiData)
                .map(sensor => sensor.latestValidTimestamp)
                .filter(timestamp => timestamp !== null && timestamp !== undefined);
              
              // If we have valid timestamps, use the most recent one
              if (validTimestamps.length > 0) {
                setLastUpdated(validTimestamps[0] || new Date().toLocaleString());
              } else {
                // Fallback to reading timestamps if no valid timestamps
                const timestamps = Object.values(apiData)
                  .filter(sensor => sensor.readings && sensor.readings.length > 0)
                  .flatMap(sensor => sensor.readings.map(reading => reading.timestamp));
                
                if (timestamps.length > 0) {
                  const mostRecent = timestamps.sort((a, b) => 
                    new Date(b).getTime() - new Date(a).getTime()
                  )[0];
                  setLastUpdated(formatTimestamp(mostRecent));
                } else {
                  setLastUpdated(new Date().toLocaleString());
                }
              }
            } else if (USE_FALLBACK_DATA) {
              // If we have some API data but it might be incomplete, merge with mock data
              const mergedData = getMockOrMergedData(location.ID, apiData);
              setSensorData(mergedData);
              
              // Check if we're using any mock data
              const usingAnyMock = Object.keys(mergedData).length > Object.keys(apiData).length;
              setUsingMockData(usingAnyMock);
              setDataSource(usingAnyMock ? 'mixed' : 'api');
              
              if (usingAnyMock) {
                setLastUpdated('Using partially simulated data');
              } else {
                // Get the most recent timestamp from any sensor
                const allTimestamps = Object.values(apiData)
                  .filter(sensor => sensor.readings && sensor.readings.length > 0)
                  .map(sensor => sensor.readings[0].timestamp);
                
                if (allTimestamps.length > 0) {
                  const mostRecent = allTimestamps.sort((a, b) => 
                    new Date(b).getTime() - new Date(a).getTime()
                  )[0];
                  setLastUpdated(formatTimestamp(mostRecent));
                } else {
                  setLastUpdated('No timestamp available');
                }
              }
            } else {
              setSensorData(apiData);
              setDataSource('api');
              
              // Get the most recent timestamp from any sensor
              const allTimestamps = Object.values(apiData)
                .filter(sensor => sensor.readings && sensor.readings.length > 0)
                .map(sensor => sensor.readings[0].timestamp);
              
              if (allTimestamps.length > 0) {
                const mostRecent = allTimestamps.sort((a, b) => 
                  new Date(b).getTime() - new Date(a).getTime()
                )[0];
                setLastUpdated(formatTimestamp(mostRecent));
              } else {
                setLastUpdated('No timestamp available');
              }
            }
          }
          
          setIsLoading(false);
          // Reset auto-retry count on successful data load
          setRetryCount(0);
        }
      } catch (err) {
        console.error('❌ Error fetching sensor data:', err);
        
        if (isMounted) {
          if (FORCE_LIVE_DATA) {
            debug("⚠️ FORCE_LIVE_DATA is true - not falling back to mock data despite API error");
            setError(`API error: ${err instanceof Error ? err.message : 'Unknown error'}`);
            setLastUpdated('Error fetching data');
            setIsLoading(false);
          } else if (USE_FALLBACK_DATA) {
            // Use mock data if API call fails
            debug("🔄 API call failed, falling back to mock data");
            const mockData = getMockOrMergedData(location.ID);
            setSensorData(mockData);
            setIsLoading(false);
            setLastUpdated('Using simulated data (API error)');
            setUsingMockData(true);
            setDataSource('mock');
          } else {
            setError('Failed to load sensor data. Please try again later.');
            setIsLoading(false);
            
            // Auto-retry up to 3 times with exponential backoff
            if (retryCount < 3) { // Correctly use retryCount state
              const nextRetryDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
              debug(`🔄 Retrying in ${nextRetryDelay}ms (attempt ${retryCount + 1}/3)`);
              
              retryTimeoutId = window.setTimeout(() => { // Store timeout ID
                setRetryCount(prev => prev + 1); // Correctly use setRetryCount
              }, nextRetryDelay);
            }
          }
        }
      }
    }
    
    loadSensorData();
    
    // Set up polling for real-time updates (every 15 minutes — matches
    // the sensor upload interval, no point polling more frequently)
    const interval = setInterval(loadSensorData, 15 * 60 * 1000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
      if (retryTimeoutId) window.clearTimeout(retryTimeoutId); // Clear timeout using its ID
    };
  }, [location.ID, refreshTrigger, retryCount]); // Added refreshTrigger and existing retryCount

  // Create static coordinate parameters
  const coordinateParameters = [
    { 
      label: 'LONGITUDE', 
      value: location.gCoord.y.toFixed(4) + '°', 
      description: 'Geographic coordinate' 
    },
    { 
      label: 'LATITUDE', 
      value: location.gCoord.x.toFixed(4) + '°', 
      description: 'Geographic coordinate' 
    }
  ];

  // Create temperature chart data if available
  const temperatureReadings = sensorData?.TEMPERATURE?.readings || [];
  const temperatureChartData = temperatureReadings.length > 0 
    ? prepareChartData(temperatureReadings)
    : [];

  // Toggle between forced mock and api data
  const toggleMockData = () => {
    // This is only for development, toggle the FORCE_MOCK_DATA flag in the code for production
    // Trigger a re-fetch by updating retryCount, which is in useEffect's dependency array
    setRetryCount(prev => prev + 1); 
  };

  // Data source badge: same style/size for every state — only the dot color
  // and label change based on whether we're showing live or mock data.
  const getDataSourceBadge = () => {
    const isLive = dataSource === 'api';
    const label = isLive
      ? 'Live Data'
      : dataSource === 'mixed'
        ? 'Partial Data'
        : 'Mock Data';
    return { bg: 'bg-gray-100', text: 'text-gray-800', label, isLive };
  };

  const badge = getDataSourceBadge();

  return (
    <div className='flex flex-col p-6 pb-0'>
      <div className="flex space-x-1 bg-gray-100/50 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('parameters')}
          className={`flex-1 py-2 px-4 rounded-md text-sm transition-colors
            ${activeTab === 'parameters' 
              ? 'bg-white text-[#005E6E] shadow-sm font-bold' 
              : 'text-gray-500 hover:text-gray-700 font-medium'
            }`}
        >
          Parameters
        </button>
        <button
          onClick={() => setActiveTab('analysis')}
          className={`flex-1 py-2 px-4 rounded-md text-sm transition-colors
            ${activeTab === 'analysis' 
              ? 'bg-white text-[#005E6E] shadow-sm font-bold' 
              : 'text-gray-500 hover:text-gray-700 font-medium'
            }`}
        >
          Real-Time Image
        </button>
      </div>

      {activeTab == 'parameters' &&
      <div className="space-y-6  p-6 pt-4">
        <div className="mb-8">
          <h2 className="text-4xl font-bold">{location.siteName}</h2>
          
          {/* Last Updated and Badge - Under site name (since refresh button is gone) */}
          <div className="flex items-center mt-1 space-x-2">
            <p className="text-xs text-[#5D5346]">
              {isLoading ? 'Loading data...' : `Last updated: ${lastUpdated}`}
            </p>
            <span className={`px-2 py-0.5 ${badge.bg} ${badge.text} rounded-full text-xs flex items-center`}>
              <span
                className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                  badge.isLive ? 'bg-green-500' : 'bg-red-500'
                }`}
              ></span>
              {badge.label}
            </span>
          </div>

          {/* Site Description - Under Last Updated info */}
          <p className="text-sm text-[#5D5346] mt-2">{location.siteDescription}</p>
          
          {error && (
            <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-md">
              <p className="text-sm text-red-600">
                {error}
              </p>
              <button 
                onClick={() => setRetryCount(prev => prev + 1)} // Use setRetryCount to trigger retry
                className="text-xs text-[#005E6E] underline mt-1"
              >
                Retry
              </button>
            </div>
          )}
          
          {DEBUG_MODE && dataSource === 'api' && Object.keys(sensorData || {}).length === 0 && !error && !isLoading && (
            <div className="mt-2 p-3 bg-amber-50 border border-amber-100 rounded-md">
              <p className="text-sm text-amber-600">
                API returned no data. Consider enabling USE_FALLBACK_DATA to show mock data.
              </p>
            </div>
          )}
        </div>

        {isLoading ? (
          <LoadingIndicator />
        ) : (
        <div className="grid grid-cols-[repeat(2,minmax(0,auto))] gap-4">            {/* Static coordinate parameters */}
            {coordinateParameters.map((param) => (
              <ParameterCard
                key={param.label}
                {...param}
                onClick={() => onParameterClick(param)}
              />
            ))}

            {/* Dynamic sensor parameters - each card shows the average value */}
            {sensorData && Object.keys(sensorData).length > 0 ? (
              Object.entries(sensorData)
                .sort(([, a], [, b]) => {
                  // Move items starting with "0" to the bottom
                  const aIsZero = a.latestValue?.startsWith('0');
                  const bIsZero = b.latestValue?.startsWith('0');
                  if (aIsZero && !bIsZero) return 1;
                  if (!aIsZero && bIsZero) return -1;
                  return 0;
                })
                .map(([key, data]) => {
                // debug(`🎨 Rendering card for [${key}], value: ${data.latestValue}`);
                // Get the chart data for this sensor
                const sensorChartData = prepareChartData(data.readings);
                
                // Format description to show this is an average if we have multiple readings
                const readingsCount = data.readings.length;
                const description = readingsCount > 1 
                  ? `${key.charAt(0) + key.slice(1).toLowerCase()} (avg of ${readingsCount} readings)`
                  : `${key.charAt(0) + key.slice(1).toLowerCase()} reading`;
                
                return (
                  <ParameterCard
                    key={key}
                    label={key}
                    unit={data.unit}
                    value={data.latestValue} // This is already the average with unit
                    description={description}
                    chartData={sensorChartData}
                    onClick={() => onParameterClick({
                      label: key,
                      value: data.latestValue,
                      unit: data.unit,
                      description: description,
                      chartData: sensorChartData
                    })}
                  />
                );
              })
            ) : !error ? (
              <div className="col-span-2 text-center py-6">
                <p className="text-[#5D5346]">No sensor data available for this location.</p>
                <button 
                  onClick={() => setRetryCount(prev => prev + 1)} // Use setRetryCount to trigger retry
                  className="mt-2 px-4 py-1 bg-[#005E6E] text-white text-sm rounded-md"
                >
                  Retry
                </button>
              </div>
            ) : null}

            {/* Temperature Chart Card - shown only if we have temperature data */}
            {temperatureChartData.length > 0 && (
              <div className="col-span-2">
                <ParameterCard
                  label="TEMPERATURE TREND"
                  value={sensorData?.TEMPERATURE?.latestValue || "N/A"}
                  description="Recent temperature readings"
                  onClick={() => onParameterClick({
                    label: "TEMPERATURE TREND",
                    value: sensorData?.TEMPERATURE?.latestValue || null,
                    description: "Recent temperature readings",
                    chartData: temperatureChartData
                  })}
                >
                  <div className="h-48 mt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={temperatureChartData} margin={{ top: 5, right: 5, left: 0, bottom: 20 }}>
                        <XAxis
                          dataKey="time"
                          stroke="#5D5346"
                          fontSize={9}
                          tickLine={false}
                          angle={-35}
                          textAnchor="end"
                          height={35}
                          interval="preserveStartEnd"
                        />
                        <YAxis 
                          stroke="#5D5346" 
                          fontSize={10}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(value) => `${value}°C`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#005E6E" 
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </ParameterCard>
              </div>
            )}
          </div>
        )}
      </div>
      }
      {activeTab == 'analysis' &&
      <div>Real time image</div>
      }
    </div>


  );
} 